import { AppModule, SettingsCounters } from '@/store/modules/app';
import { resetJobSearch } from '@/sections/jobs/search';
import { WorkersModule } from '@/store/modules/workers';
import { openCourseCatalog, resetCourseSearch } from '@/sections/courses/search';

// Tabs under the main navigation of a section (Arbeit: Stellensuche / Stellenkatalog …).
// A route opts in with meta.section; TsSectionTabs renders the tabs of that section.
export interface SectionTab {
  route: string // route name
  label: string
  icon: string // bootstrap-icons class
  activeOn?: string[] // more routes that belong to this tab (e.g. the results of a search)
  onSelect?: () => void // state reset when the tab is opened from the tab bar
}

const counterVisible = (name: SettingsCounters['name']): boolean => {
  const counters: SettingsCounters[] = AppModule.settingPortal.counters || [];
  return !!counters.find((counter) => counter.name === name)?.visible;
};

const jobsTabs = (): SectionTab[] => {
  const t = AppModule.translation;
  const tabs: Array<SectionTab & { visible: boolean }> = [
    {
      route: 'Jobs', label: t[13934], icon: 'bi-search', activeOn: ['JobsResults'], onSelect: resetJobSearch, visible: true,
    },
    {
      route: 'JobsCatalog', label: t[13935], icon: 'bi-list-ul', onSelect: resetJobSearch, visible: true,
    },
    // The next tabs exist only on sites that show the matching counter (PAT counters)
    {
      route: 'JobsTraining',
      label: t[12079],
      icon: 'bi-journal-bookmark',
      onSelect: () => AppModule.SetVisibleMap(false),
      visible: counterVisible('education'),
    },
    {
      route: 'JobsLocations',
      label: t[10459] || 'Orte',
      icon: 'bi-geo-alt',
      onSelect: () => AppModule.SetVisibleMap(false),
      visible: counterVisible('location'),
    },
    {
      route: 'JobsCompanies', label: t[13937], icon: 'bi-building', visible: counterVisible('companies'),
    },
    {
      route: 'JobsMap',
      label: t[13719],
      icon: 'bi-map',
      onSelect: () => {
        AppModule.SetVisibleJobs(false);
        AppModule.SetVisibleMap(true);
      },
      visible: counterVisible('map'),
    },
  ];
  return tabs.filter((tab) => tab.visible).map(({ visible, ...tab }) => tab);
};

// Personal: Personalsuche (with its results) / Berufekatalog; a tab opens without a list
const workersTabs = (): SectionTab[] => {
  const t = AppModule.translation;
  const closeList = () => WorkersModule.RESET_SHOW_WORKERS();
  return [
    {
      route: 'Workers', label: t[13635], icon: 'bi-search', activeOn: ['WorkersResults'], onSelect: closeList,
    },
    {
      route: 'WorkersCatalog', label: t[13936], icon: 'bi-list-ul', onSelect: closeList,
    },
  ];
};

// Bildung: Kurssuche (with its results) / Kurskatalog; the catalog keeps a chosen field
const coursesTabs = (): SectionTab[] => {
  const t = AppModule.translation;
  return [
    {
      route: 'Courses', label: t[15403], icon: 'bi-search', activeOn: ['CoursesResults'], onSelect: resetCourseSearch,
    },
    {
      route: 'CoursesCatalog', label: t[15402], icon: 'bi-list-ul', onSelect: openCourseCatalog,
    },
  ];
};

const sections: Record<string, () => SectionTab[]> = {
  jobs: jobsTabs,
  workers: workersTabs,
  courses: coursesTabs,
};

export const sectionTabs = (section: string): SectionTab[] => sections[section]?.() || [];
